import { useEffect, useState } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";
import * as Icons from "lucide-react";
import Employees from "./pages/Employees";
import seoPages from "./seo-pages.json";

const SITE_URL = "https://au-someteacher.com";

function RouteSEO() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.replace(/\/$/, "") || "/";
    const page = seoPages[path] || {
      title: "Page Not Found | Au-Some Teacher",
      description: "Explore ABA therapy services from Au-Some Teacher.",
      noindex: true,
    };
    const canonical = `${SITE_URL}${path}`;
    document.title = page.title;
    const updateMeta = (selector, attributes) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement(attributes.href ? "link" : "meta");
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    };
    updateMeta('meta[name="description"]', { name: "description", content: page.description });
    updateMeta('meta[name="robots"]', { name: "robots", content: page.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });
    updateMeta('link[rel="canonical"]', { rel: "canonical", href: canonical });
    updateMeta('meta[property="og:title"]', { property: "og:title", content: page.title });
    updateMeta('meta[property="og:description"]', { property: "og:description", content: page.description });
    updateMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    updateMeta('meta[name="twitter:title"]', { name: "twitter:title", content: page.title });
    updateMeta('meta[name="twitter:description"]', { name: "twitter:description", content: page.description });
  }, [pathname]);
  return null;
}

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbxB1fXiDyfEPSgySoJg7RAIxWTDoPRwKOPzZu7VZWXR3KZodJ7-cu3bSkE8rzJfqw6h/exec";
const contact = {
  email: "info@au-someteacher.com",
  emailHref: "mailto:info@au-someteacher.com",
};

const assets = {
  logo: "https://customer-assets.emergentagent.com/job_9999b4e2-e562-4f7d-b65c-b27bad5fffb6/artifacts/7m2ruj3w_brain-with-pencil1.png",
  hero: "https://images.pexels.com/photos/7447263/pexels-photo-7447263.jpeg",
  parent: "https://images.pexels.com/photos/8653951/pexels-photo-8653951.jpeg",
  team: "https://res.cloudinary.com/hzrnii6l/image/upload/v1784964667/B8D8339B-52BC-4555-B900-2472F3F0D8A5_gp5xah.png",
  services: "https://images.pexels.com/photos/4934170/pexels-photo-4934170.jpeg",
  careers: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg",
  about: "https://images.pexels.com/photos/8363095/pexels-photo-8363095.jpeg",
};
const HIRING_OPEN = true;

const serviceAreas = [
  "Spring",
  "Klein",
  "The Woodlands",
  "Conroe",
  "Humble",
  "Tomball",
  "Magnolia",
];

const navItems = [
  { label: "Home", path: "/" },
  { label: "Insurance", path: "/insurance" },
  { label: "Pricing", path: "/pricing" },
  { label: "About Us", path: "/about" },
  { label: "Careers", path: "/careers" },
  { label: "Employees", path: "/employees" },
];

const services = [
  {
    title: "ABA Therapy",
    shortTitle: "In-Home ABA Therapy",
    path: "/services/aba-therapy",
    icon: Icons.Brain,
    image: assets.hero,
    summary:
      "Individualized in-home therapy focused on communication, behavior, independence, social skills, and daily routines.",
    detail:
      "Our ABA programs are designed around each child's strengths, needs, family routines, and long-term goals. Care plans are BCBA-led and adjusted as progress is made.",
    pageTitle: "In-home ABA therapy built around your child and family.",
    pageText:
      "Our BCBA-led programs help children build practical skills where daily life happens—at home, in the community, and alongside the people who support them most.",
    sectionTitle: "Meaningful skills for everyday life.",
    sectionText:
      "Treatment goals are selected with families and updated as the child grows. Sessions combine structured teaching, play, natural routines, caregiver collaboration, and ongoing progress monitoring.",
    focusAreas: [
      [Icons.MessagesSquare, "Communication", "Requesting, expressing needs, conversation, and understanding directions."],
      [Icons.Hand, "Behavior Support", "Teaching safer, more functional ways to communicate and respond."],
      [Icons.Users, "Social Skills", "Play, turn-taking, shared attention, and participation with others."],
      [Icons.Home, "Daily Living", "Routines that support independence at home and in the community."],
      [Icons.ShieldCheck, "Safety Skills", "Building practical responses for common safety situations."],
      [Icons.Sparkles, "Confidence", "Helping children practice new skills through positive, supportive experiences."],
    ],
    steps: [
      "Family goals and routines guide the treatment plan.",
      "A BCBA develops individualized teaching strategies.",
      "The therapy team practices skills during real activities.",
      "Progress is reviewed and goals are adjusted over time.",
    ],
    faqs: [
      ["Where does ABA therapy take place?", "Our primary service model is in-home ABA therapy, with caregiver participation and collaboration with other settings when appropriate."],
      ["How are treatment goals chosen?", "Goals are based on assessment results, caregiver priorities, safety needs, daily routines, and skills that can improve independence and quality of life."],
      ["Who oversees treatment?", "A Board Certified Behavior Analyst leads the treatment plan, monitors progress, and provides ongoing clinical supervision."],
    ],
  },
  {
    title: "Parent Training",
    shortTitle: "Parent & Caregiver Training",
    path: "/services/parent-training",
    icon: Icons.HandHeart,
    image: assets.parent,
    summary:
      "Practical coaching that helps caregivers feel confident supporting progress between therapy sessions.",
    detail:
      "Families learn strategies they can use during meals, transitions, play, homework, community outings, and everyday moments at home.",
    pageTitle: "Practical coaching for the moments that matter most.",
    pageText:
      "Caregiver training turns therapy strategies into tools families can use during meals, bedtime, transitions, play, schoolwork, and community routines.",
    sectionTitle: "Support that fits your family—not the other way around.",
    sectionText:
      "Parent and caregiver training is collaborative. We begin with the situations that matter to your family, model useful strategies, practice together, and adjust the plan based on what works in real life.",
    focusAreas: [
      [Icons.RefreshCw, "Transitions", "Preparing for changes, reducing stress, and building predictable routines."],
      [Icons.Utensils, "Meals", "Supporting communication, participation, and manageable family routines."],
      [Icons.MoonStar, "Bedtime", "Creating clear expectations and consistent evening routines."],
      [Icons.Gamepad2, "Play", "Building connection, communication, flexibility, and shared enjoyment."],
      [Icons.BookOpenCheck, "Homework", "Using practical supports for attention, task completion, and breaks."],
      [Icons.MapPin, "Community Outings", "Preparing for stores, appointments, family events, and recreation."],
    ],
    steps: [
      "We identify the routines your family wants to improve.",
      "The BCBA explains and models practical strategies.",
      "Caregivers practice with support and feedback.",
      "The plan is refined so it works in everyday life.",
    ],
    faqs: [
      ["Do parents need ABA experience?", "No. Strategies are explained in clear, practical language and practiced within routines your family already knows."],
      ["Is caregiver training part of treatment?", "Caregiver participation is an important part of helping skills carry over beyond direct therapy sessions."],
      ["Can training focus on a specific routine?", "Yes. Coaching can focus on priorities such as transitions, communication, homework, meals, bedtime, safety, or community outings."],
    ],
  },
  {
    title: "Assessments",
    shortTitle: "ABA Assessments",
    path: "/services/assessments",
    icon: Icons.ClipboardCheck,
    image: assets.services,
    summary:
      "Thoughtful skill and behavior assessments that guide goals, treatment planning, and insurance authorization.",
    detail:
      "We use observation, caregiver input, records review, and standardized tools to understand what support will be most meaningful.",
    pageTitle: "A thoughtful assessment is the foundation of effective care.",
    pageText:
      "Our assessment process brings together caregiver priorities, direct observation, records, and skill-based information to create an individualized treatment plan.",
    sectionTitle: "Understanding strengths, needs, and the right next step.",
    sectionText:
      "Assessment is not about reducing a child to a score. It is a structured way to understand current skills, barriers, family priorities, and the support that may make the greatest difference.",
    focusAreas: [
      [Icons.MessageSquareText, "Caregiver Interview", "Learning about concerns, priorities, routines, and family goals."],
      [Icons.Eye, "Direct Observation", "Watching skills and behavior within relevant activities and settings."],
      [Icons.FolderSearch, "Records Review", "Reviewing available diagnostic, educational, medical, and therapy information."],
      [Icons.ListChecks, "Skill Assessment", "Identifying current strengths and areas that may benefit from instruction."],
      [Icons.Target, "Goal Development", "Turning assessment findings into measurable, meaningful treatment goals."],
      [Icons.FileCheck2, "Treatment Planning", "Preparing recommendations and documentation for the next stage of care."],
    ],
    steps: [
      "We gather intake information and available records.",
      "A BCBA meets with the family and observes the child.",
      "Assessment information is reviewed and organized.",
      "An individualized treatment plan is developed and discussed.",
    ],
    faqs: [
      ["Is this a diagnostic autism evaluation?", "Our ABA assessment supports treatment planning. Families seeking an autism diagnosis should work with a qualified diagnostic provider."],
      ["What should families provide?", "Helpful items may include diagnostic reports, insurance information, school records, prior evaluations, therapy reports, and a list of current concerns."],
      ["What happens after the assessment?", "The BCBA reviews recommendations, develops goals, and completes the documentation needed for treatment planning and applicable authorization steps."],
    ],
  },
  {
    title: "School Collaboration",
    shortTitle: "School Collaboration",
    path: "/services/school-collaboration",
    icon: Icons.School,
    image: assets.about,
    summary:
      "Coordination with educators and school teams to support consistency across learning environments.",
    detail:
      "When appropriate, we partner with schools and special education teams to align strategies and strengthen real-world skill use.",
    pageTitle: "Helping skills carry across home, school, and everyday life.",
    pageText:
      "With family permission and when appropriate, we collaborate with educators and other providers so children experience clearer, more consistent support across environments.",
    sectionTitle: "Collaboration centered on the child.",
    sectionText:
      "School collaboration is designed to improve communication—not replace the school team's role. We share relevant strategies, listen to educator observations, and support consistent skill use across settings.",
    focusAreas: [
      [Icons.MessagesSquare, "Team Communication", "Sharing relevant observations, priorities, and strategies with permission."],
      [Icons.Repeat2, "Skill Generalization", "Helping children use learned skills with different people and in new places."],
      [Icons.NotebookTabs, "Consistent Strategies", "Supporting clear language, routines, and reinforcement across environments."],
      [Icons.GraduationCap, "Educational Insight", "Bringing an education-informed perspective to treatment collaboration."],
      [Icons.Puzzle, "Coordinated Support", "Working alongside caregivers, educators, and related service providers."],
      [Icons.Goal, "Shared Priorities", "Keeping communication focused on functional, meaningful student needs."],
    ],
    steps: [
      "The family identifies a need for school collaboration.",
      "Appropriate permissions and communication channels are established.",
      "The BCBA gathers relevant input and shares useful strategies.",
      "The team reviews whether skills are carrying across settings.",
    ],
    faqs: [
      ["Will you attend school meetings?", "Participation depends on clinical relevance, family permission, provider availability, and school procedures."],
      ["Do you write or control the IEP?", "No. The school team develops and implements the IEP. Our role is to provide relevant clinical input and collaborate within appropriate boundaries."],
      ["Why does collaboration matter?", "Children often learn more successfully when the adults supporting them use compatible expectations, communication, and teaching strategies."],
    ],
  },
];

const whyChooseUs = [
  [Icons.BadgeCheck, "BCBA-Led Treatment", "Clinical oversight from experienced behavior analysts."],
  [Icons.BookOpenCheck, "Individualized Programs", "Goals are built around each child and family."],
  [Icons.HeartHandshake, "Family Partnership", "Parents are included, respected, and supported."],
  [Icons.Home, "Real-Life Skill Development", "Therapy happens where children live and learn."],
  [Icons.CalendarCheck, "Flexible Service Delivery", "In-home care shaped around family routines."],
  [Icons.GraduationCap, "Education-Informed Care", "Teaching expertise meets evidence-based ABA."],
];

const faqs = [
  {
    q: "What is ABA therapy?",
    a: "Applied Behavior Analysis is an evidence-based therapy that helps children build meaningful skills while reducing behaviors that interfere with learning, safety, or independence.",
  },
  {
    q: "Do you accept Medicaid?",
    a: "Yes. We currently list Texas Children's Health Plan among accepted plans and can help families verify benefits before beginning services.",
  },
  {
    q: "Are you accepting new clients?",
    a: "Yes. Au-Some Teacher ABA Services is currently accepting new clients throughout the Greater Houston area.",
  },
  {
    q: "Where do services take place?",
    a: "Services are provided in the home and may include collaboration with caregivers, schools, and other providers when appropriate.",
  },
  {
    q: "What ages do you serve?",
    a: "We support children and teens with autism from ages 2 through 18.",
  },
];

const testimonials = [
  {
    quote:
      "Au-Some Teacher helped our family understand the next step. We felt supported from the first conversation.",
    name: "Parent of a 5-year-old",
  },
  {
    quote:
      "The team is professional, warm, and truly focused on practical progress at home and school.",
    name: "Houston-area parent",
  },
  {
    quote:
      "We appreciate the communication, parent coaching, and the way goals connect to real daily routines.",
    name: "Caregiver testimonial",
  },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function Logo({ footer = false }) {
  return (
    <Link
      to="/"
      className="flex items-center gap-3 rounded-full focus:outline-none focus:ring-2 focus:ring-teal"
      data-testid={footer ? "footer-logo-link" : "header-logo-link"}
      aria-label="Au-Some Teacher ABA Services home"
    >
      <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-navy/10">
        <img
          src={assets.logo}
          alt="Au-Some Teacher ABA Services logo"
          className="h-12 w-12 object-cover object-center"
          data-testid={footer ? "footer-logo-image" : "header-logo-image"}
        />
      </span>
      <span className="leading-tight">
        <span
          className={`block font-display text-base font-semibold tracking-tight ${footer ? "text-white" : "text-navy"}`}
          data-testid={footer ? "footer-brand-name" : "header-brand-name"}
        >
          Au-Some Teacher
        </span>
        <span
          className={`block text-xs font-semibold ${footer ? "text-white/70" : "text-navy/60"}`}
          data-testid={footer ? "footer-brand-subtitle" : "header-brand-subtitle"}
        >
          ABA Services
        </span>
      </span>
    </Link>
  );
}

function DesktopNavigation() {
  return (
    <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation" data-testid="desktop-navigation">
      <NavLink
        to="/"
        className={({ isActive }) =>
          `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
            isActive ? "bg-teal/10 text-navy" : "text-navy/70 hover:bg-slate hover:text-navy"
          }`
        }
        data-testid="desktop-nav-home-link"
      >
        Home
      </NavLink>

      <ServicesDropdown />

      {navItems.filter((item) => item.path !== "/").map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          className={({ isActive }) =>
            `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
              isActive ? "bg-teal/10 text-navy" : "text-navy/70 hover:bg-slate hover:text-navy"
            }`
          }
          data-testid={`desktop-nav-${item.label.toLowerCase().replaceAll(" ", "-")}-link`}
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function ServicesDropdown() {
  const { pathname } = useLocation();
  const servicesActive = pathname === "/services" || pathname.startsWith("/services/");

  return (
    <div className="group relative">
      <div
        className={`flex items-center rounded-full transition-colors duration-200 ${
          servicesActive ? "bg-teal/10 text-navy" : "text-navy/70 hover:bg-slate hover:text-navy"
        }`}
      >
        <Link
          to="/services"
          className="rounded-l-full py-2 pl-4 pr-2 text-sm font-semibold"
          data-testid="desktop-nav-services-link"
        >
          Services
        </Link>
        <button
          type="button"
          className="rounded-r-full py-2 pl-1 pr-4"
          data-testid="desktop-services-dropdown-button"
          aria-label="Open services menu"
          aria-haspopup="true"
        >
          <Icons.ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
        </button>
      </div>

      <div className="invisible absolute left-0 top-full z-50 w-80 translate-y-2 rounded-3xl border border-navy/10 bg-white p-3 opacity-0 shadow-2xl shadow-navy/10 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
        <Link
          to="/services"
          className="mb-2 block rounded-2xl bg-slate px-4 py-3 transition-colors hover:bg-teal/10"
          data-testid="desktop-dropdown-services-overview-link"
        >
          <span className="block text-sm font-bold text-navy">All Services</span>
          <span className="mt-1 block text-xs leading-relaxed text-navy/60">View our complete ABA service overview.</span>
        </Link>

        {services.map((service) => {
          const Icon = service.icon;
          return (
            <Link
              key={service.title}
              to={service.path}
              className="flex items-start gap-3 rounded-2xl px-4 py-3 transition-colors hover:bg-slate"
              data-testid={`desktop-dropdown-${service.title.toLowerCase().replaceAll(" ", "-")}-link`}
            >
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal/10 text-teal">
                <Icon className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-bold text-navy">{service.shortTitle}</span>
                <span className="mt-1 block text-xs leading-relaxed text-navy/60">{service.summary}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function MobileNavigation({ closeMenu }) {
  return (
    <nav className="flex flex-col gap-2" aria-label="Mobile navigation" data-testid="mobile-navigation">
      <Link
        to="/"
        onClick={closeMenu}
        className="rounded-2xl bg-slate px-5 py-4 font-display text-lg font-semibold text-navy"
        data-testid="mobile-nav-home-link"
      >
        Home
      </Link>

      <div className="rounded-2xl bg-slate p-3" data-testid="mobile-services-menu">
        <Link
          to="/services"
          onClick={closeMenu}
          className="flex items-center justify-between rounded-xl px-2 py-2 font-display text-lg font-semibold text-navy"
          data-testid="mobile-nav-services-link"
        >
          Services <Icons.ArrowRight className="h-5 w-5 text-teal" />
        </Link>
        <div className="mt-1 flex flex-col gap-1 border-t border-navy/10 pt-2">
          {services.map((service) => (
            <Link
              key={service.path}
              to={service.path}
              onClick={closeMenu}
              className="rounded-xl px-3 py-2 text-sm font-semibold text-navy/70 hover:bg-white hover:text-navy"
              data-testid={`mobile-service-${service.title.toLowerCase().replaceAll(" ", "-")}-link`}
            >
              {service.shortTitle}
            </Link>
          ))}
        </div>
      </div>

      {navItems.filter((item) => item.path !== "/").map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={closeMenu}
          className="rounded-2xl bg-slate px-5 py-4 font-display text-lg font-semibold text-navy"
          data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}-link`}
        >
          {item.label}
        </Link>
      ))}

      <Link
        to="/become-a-client"
        onClick={closeMenu}
        className="rounded-2xl bg-teal px-5 py-4 text-center font-display text-lg font-semibold text-white"
        data-testid="mobile-nav-become-client-link"
      >
        Become a Client
      </Link>
    </nav>
  );
}

function MobileMenu({ open, setOpen }) {
  const closeMenu = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-full border-navy/10 lg:hidden" data-testid="mobile-menu-open-button" aria-label="Open menu">
          <Icons.Menu className="h-5 w-5 text-navy" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[88vw] border-l-0 bg-white p-6" data-testid="mobile-menu-panel">
        <div className="mt-8 flex flex-col gap-6">
          <Logo />
          <MobileNavigation closeMenu={closeMenu} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-navy/5 bg-white/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-8">
        <Logo />
        <DesktopNavigation />
        <div className="hidden items-center gap-3 lg:flex">
          <Button asChild className="rounded-full bg-teal px-6 py-5 font-bold text-white hover:bg-teal-dark" data-testid="header-become-client-button">
            <Link to="/become-a-client">Become a Client</Link>
          </Button>
        </div>
        <MobileMenu open={open} setOpen={setOpen} />
      </div>
    </header>
  );
}

function SectionLabel({ children, testId }) {
  return (
    <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-teal" data-testid={testId}>
      {children}
    </p>
  );
}

function PageHero({ eyebrow, title, text, image, cta = true, testId }) {
  return (
    <section className="relative overflow-hidden bg-slate py-16 md:py-24" data-testid={testId}>
      <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-gold/20 blur-3xl" />
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 md:grid-cols-[1.05fr_0.95fr] md:px-8">
        <div className="relative z-10">
          <SectionLabel testId={`${testId}-eyebrow`}>{eyebrow}</SectionLabel>
          <h1 className="max-w-4xl font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl lg:text-6xl" data-testid={`${testId}-title`}>
            {title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-navy/75 md:text-lg" data-testid={`${testId}-text`}>
            {text}
          </p>
          {cta && (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="rounded-full bg-teal px-8 py-6 font-bold text-white hover:bg-teal-dark" data-testid={`${testId}-primary-button`}>
                <Link to="/become-a-client">Become a Client</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-navy/15 bg-white px-8 py-6 font-bold text-navy hover:bg-white" data-testid={`${testId}-secondary-button`}>
                <Link to="/insurance">Verify Insurance</Link>
              </Button>
            </div>
          )}
        </div>
        <div className="relative z-10">
          <div className="aspect-[4/3] overflow-hidden rounded-[2rem] bg-white p-2 shadow-2xl shadow-navy/10">
            <img src={image} alt="Warm therapy support" className="h-full w-full rounded-[1.5rem] object-cover object-center" data-testid={`${testId}-image`} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-white" data-testid="homepage-hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(244,185,66,0.22),transparent_28%),radial-gradient(circle_at_80%_4%,rgba(44,177,188,0.20),transparent_24%)]" />
      <div className="mx-auto grid min-h-[760px] max-w-7xl items-center gap-12 px-4 py-16 md:grid-cols-[1.03fr_0.97fr] md:px-8 md:py-24">
        <div className="relative z-10">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal/20 bg-teal/10 px-4 py-2 text-sm font-bold text-navy" data-testid="hero-accepting-clients-badge">
            <Icons.Sparkles className="h-4 w-4 text-teal" /> Currently Accepting New Clients
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-semibold leading-[0.96] tracking-tight text-navy sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            Teaching Skills. Building Confidence. Changing Futures.
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-navy/75 md:text-lg" data-testid="hero-subheadline">
            In-home ABA therapy helping children build communication, independence, social skills, and confidence throughout the Greater Houston area.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-testid="hero-cta-group">
            <Button asChild className="rounded-full bg-teal px-8 py-6 text-base font-bold text-white shadow-xl shadow-teal/20 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-teal-dark" data-testid="hero-become-client-button">
              <Link to="/become-a-client">Become a Client <Icons.ArrowRight className="h-5 w-5" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-navy/15 bg-white px-8 py-6 text-base font-bold text-navy shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:bg-slate" data-testid="hero-verify-insurance-button">
              <Link to="/insurance">Verify Insurance</Link>
            </Button>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-3" data-testid="hero-trust-indicators">
            {["BCBA-Led Care", "Family-Centered Approach", "Currently Accepting New Clients"].map((item) => (
              <div key={item} className="flex items-center gap-2 rounded-2xl bg-slate px-4 py-3 text-sm font-bold text-navy" data-testid={`hero-trust-${item.toLowerCase().replaceAll(" ", "-")}`}>
                <Icons.Check className="h-4 w-4 rounded-full bg-teal p-0.5 text-white" /> {item}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <div className="absolute -right-6 -top-8 hidden rounded-3xl bg-gold px-6 py-4 font-display text-2xl font-semibold text-navy shadow-xl rotate-3 md:block" data-testid="hero-floating-card">
            We can help.
          </div>
          <div className="aspect-[4/5] overflow-hidden rounded-[2.5rem] bg-slate p-2 shadow-2xl shadow-navy/15">
            <img src={assets.hero} alt="Family receiving supportive in-home ABA therapy" className="h-full w-full rounded-[2rem] object-cover object-center" data-testid="hero-family-image" />
          </div>
          <div className="absolute -bottom-7 left-4 right-4 rounded-[1.5rem] border border-white/70 bg-white/90 p-5 shadow-2xl shadow-navy/15 backdrop-blur-xl md:left-10 md:right-auto md:w-80" data-testid="hero-location-card">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-teal">Serving Greater Houston</p>
            <p className="mt-2 text-sm leading-relaxed text-navy/75">{serviceAreas.join(", ")} and nearby communities.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function InsurancePreview() {
  const plans = ["Texas Children's Health Plan", "Blue Cross Blue Shield of Texas", "Aetna"];
  return (
    <section className="bg-slate py-20 md:py-28" data-testid="insurance-preview-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <SectionLabel testId="insurance-preview-eyebrow">Insurance support</SectionLabel>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="insurance-preview-title">Start Services Faster</h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/75 md:text-lg" data-testid="insurance-preview-copy">
              Currently accepting new clients. We can help verify insurance benefits and guide you through the authorization process.
            </p>
            <Button asChild className="mt-8 rounded-full bg-navy px-8 py-6 font-bold text-white hover:bg-navy/90" data-testid="insurance-preview-verify-button">
              <Link to="/insurance">Verify Benefits</Link>
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {plans.map((plan, index) => (
              <Card key={plan} className="rounded-[1.75rem] border-navy/5 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`insurance-card-${index + 1}`}>
                <CardContent className="p-0">
                  <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-2xl bg-teal/10 text-teal">
                    <Icons.ShieldCheck className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-navy" data-testid={`insurance-plan-${index + 1}-name`}>{plan}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/65" data-testid={`insurance-plan-${index + 1}-text`}>Benefit checks and authorization guidance available.</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ParentConnection() {
  return (
    <section className="bg-white py-20 md:py-32" data-testid="parent-connection-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 md:grid-cols-2 md:px-8">
        <div className="aspect-[5/4] overflow-hidden rounded-[2rem] bg-slate p-2 shadow-xl shadow-navy/10">
          <img src={assets.parent} alt="Parent and child learning together" className="h-full w-full rounded-[1.5rem] object-cover object-center" data-testid="parent-connection-image" />
        </div>
        <div>
          <SectionLabel testId="parent-connection-eyebrow">Family-centered support</SectionLabel>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="parent-connection-title">You Shouldn&apos;t Have to Navigate This Journey Alone</h2>
          <p className="mt-6 text-base leading-relaxed text-navy/75 md:text-lg" data-testid="parent-connection-copy">
            When your family is looking for answers, the process can feel overwhelming. Our team helps you understand ABA therapy, insurance steps, assessment needs, and what progress can look like at home.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {["Clear next steps", "Compassionate guidance", "Practical family coaching", "Meaningful progress goals"].map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-2xl bg-slate p-4 font-semibold text-navy" data-testid={`parent-connection-${item.toLowerCase().replaceAll(" ", "-")}`}>
                <Icons.Check className="h-5 w-5 text-teal" /> {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSection({ detailed = false }) {
  return (
    <section className={`${detailed ? "bg-white" : "bg-slate"} py-20 md:py-32`} data-testid={detailed ? "services-detail-section" : "homepage-services-section"}>
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mb-12 max-w-3xl">
          <SectionLabel testId={detailed ? "services-detail-eyebrow" : "services-eyebrow"}>Our services</SectionLabel>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid={detailed ? "services-detail-title" : "services-title"}>
            ABA care designed for home, school, and everyday life.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Link
                key={service.title}
                to={service.path}
                className="group block rounded-[1.75rem] focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-4"
                aria-label={`Learn more about ${service.title}`}
                data-testid={`service-card-${index + 1}-link`}
              >
                <Card className="h-full rounded-[1.75rem] border-navy/5 bg-white p-7 shadow-sm transition duration-300 group-hover:-translate-y-1 group-hover:border-teal/20 group-hover:shadow-xl">
                  <CardContent className="p-0">
                    <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal/10 text-teal transition-transform duration-300 group-hover:scale-105">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="font-display text-2xl font-semibold text-navy" data-testid={`service-card-${index + 1}-title`}>{service.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-navy/70" data-testid={`service-card-${index + 1}-summary`}>{service.summary}</p>
                    {detailed && <p className="mt-4 text-sm leading-relaxed text-navy/70" data-testid={`service-card-${index + 1}-detail`}>{service.detail}</p>}
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-teal">
                      Learn more <Icons.ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const steps = ["Contact Us", "Verify Insurance", "Assessment", "Begin Therapy"];
  return (
    <section className="bg-white py-20 md:py-32" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <SectionLabel testId="process-eyebrow">A simple path forward</SectionLabel>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="process-title">Getting Started Is Simple</h2>
        </div>
        <div className="grid gap-5 md:grid-cols-4">
          {steps.map((step, index) => (
            <div key={step} className="relative rounded-[1.75rem] bg-slate p-7" data-testid={`process-step-${index + 1}`}>
              <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-full bg-navy font-display text-lg font-semibold text-white" data-testid={`process-step-${index + 1}-number`}>{index + 1}</div>
              <h3 className="font-display text-xl font-semibold text-navy" data-testid={`process-step-${index + 1}-title`}>{step}</h3>
              <p className="mt-3 text-sm leading-relaxed text-navy/65" data-testid={`process-step-${index + 1}-copy`}>
                {index === 0 && "Tell us about your child and what support your family needs."}
                {index === 1 && "We help review benefits and explain authorization requirements."}
                {index === 2 && "A BCBA completes an assessment and builds an individualized plan."}
                {index === 3 && "Therapy begins with clear goals, parent support, and progress monitoring."}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  return (
    <section className="bg-navy py-20 text-white md:py-32" data-testid="why-choose-us-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mb-12 max-w-3xl">
          <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-gold" data-testid="why-choose-us-eyebrow">Why families choose us</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight md:text-5xl" data-testid="why-choose-us-title">Expert care that still feels personal.</h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {whyChooseUs.map(([Icon, title, copy], index) => (
            <div key={title} className="rounded-[1.75rem] border border-white/10 bg-white/8 p-7 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:bg-white/12" data-testid={`why-card-${index + 1}`}>
              <Icon className="mb-6 h-8 w-8 text-gold" />
              <h3 className="font-display text-xl font-semibold" data-testid={`why-card-${index + 1}-title`}>{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70" data-testid={`why-card-${index + 1}-copy`}>{copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamSection() {
  return (
    <section className="bg-slate py-20 md:py-32" data-testid="team-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 md:grid-cols-[0.85fr_1.15fr] md:px-8">
        <div className="overflow-hidden rounded-[2rem] bg-white p-2 shadow-xl shadow-navy/10">
          <img
  src={assets.team}
  alt="Rosalyn Holmes, BCBA, Clinical Director"
  className="aspect-[4/5] w-full rounded-[1.5rem] object-cover object-center"
/>
        </div>
        <div>
          <SectionLabel testId="team-eyebrow">Leadership</SectionLabel>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="team-title">Clinical leadership families can trust.</h2>
          <div className="mt-8 rounded-[1.75rem] bg-white p-8 shadow-sm" data-testid="team-director-card">
            <h3 className="font-display text-2xl font-semibold text-navy" data-testid="team-director-name">Rosalyn Holmes, BCBA</h3>
            <p className="mt-1 font-bold text-teal" data-testid="team-director-role">Clinical Director</p>
            <p className="mt-5 text-base leading-relaxed text-navy/75" data-testid="team-director-copy">
              Rosalyn leads Au-Some Teacher ABA Services with a commitment to compassionate, evidence-based care. Her approach blends behavioral science, educator insight, and a deep respect for every family&apos;s goals.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="bg-white py-20 md:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mb-12 max-w-3xl">
          <SectionLabel testId="testimonials-eyebrow">Family voices</SectionLabel>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="testimonials-title">Support that feels steady, clear, and personal.</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((item, index) => (
            <Card key={item.name} className="rounded-[1.75rem] border-navy/5 bg-slate p-7 shadow-none" data-testid={`testimonial-card-${index + 1}`}>
              <CardContent className="p-0">
                <Icons.MessageCircleHeart className="mb-6 h-8 w-8 text-teal" />
                <p className="text-base leading-relaxed text-navy/75" data-testid={`testimonial-card-${index + 1}-quote`}>“{item.quote}”</p>
                <p className="mt-6 font-bold text-navy" data-testid={`testimonial-card-${index + 1}-name`}>{item.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-slate py-20 md:py-32" data-testid="faq-section">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[0.75fr_1.25fr] md:px-8">
        <div>
          <SectionLabel testId="faq-eyebrow">Questions parents ask</SectionLabel>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="faq-title">Helpful answers before you begin.</h2>
        </div>
        <Accordion type="single" collapsible className="rounded-[1.75rem] bg-white p-4 shadow-sm" data-testid="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionItem key={faq.q} value={`faq-${index}`} className="border-navy/10 px-4" data-testid={`faq-item-${index + 1}`}>
              <AccordionTrigger className="py-6 text-left font-display text-lg font-semibold text-navy hover:no-underline" data-testid={`faq-trigger-${index + 1}`}>
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-navy/70" data-testid={`faq-content-${index + 1}`}>
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-white px-4 py-20 md:px-8 md:py-28" data-testid="final-cta-section">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-navy p-8 text-white md:p-14">
        <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-gold" data-testid="final-cta-eyebrow">Ready when you are</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight md:text-5xl" data-testid="final-cta-title">Ready to Take the Next Step?</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70" data-testid="final-cta-copy">Tell us a little about your family. We’ll help you understand services, insurance, and the path to beginning care.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild className="rounded-full bg-teal px-8 py-6 font-bold text-white hover:bg-teal-dark" data-testid="final-cta-become-client-button">
              <Link to="/become-a-client">Become a Client</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/20 bg-white px-8 py-6 font-bold text-navy hover:bg-slate" data-testid="final-cta-contact-button">
              <Link to="/become-a-client">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

const emptyLeadForm = {
  name: "",
  email: "",
  phone: "",
  child_age: "",
  insurance: "",
  city: "",
  message: "",
};

function useLeadForm(kind) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyLeadForm);

  const update = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        kind,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        child_age: form.child_age.trim(),
        insurance: form.insurance.trim(),
        city: form.city.trim(),
        message: form.message.trim(),
      };

      await fetch(GOOGLE_SCRIPT_URL, {
  method: "POST",
  mode: "no-cors",
  headers: {
    "Content-Type": "text/plain;charset=utf-8",
  },
  body: JSON.stringify(payload),
});
if (typeof window.fbq === "function") {
  window.fbq("track", "Lead");
}
      toast.success("Thank you — your message was received.", {
        description: "Our team will follow up with next steps soon.",
      });

      setForm({ ...emptyLeadForm });
    } catch (error) {
      console.error("Google Apps Script submission failed:", error);

      toast.error("We couldn't send the form yet.", {
        description:
          "Please try again or email info@au-someteacher.com.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    submitting,
    update,
    submit,
  };
}

function LeadTextField({ kind, name, label, value, onChange, required = false, type = "text" }) {
  const fieldId = `${kind}-${name.replaceAll("_", "-")}`;

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldId} className="text-navy" data-testid={`${fieldId}-label`}>{label}</Label>
      <Input
        id={fieldId}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="h-12 rounded-2xl border-navy/10 bg-slate px-4"
        data-testid={`${fieldId}-input`}
      />
    </div>
  );
}

function LeadFields({ kind, form, update, compact }) {
  const isCareer = kind === "career";

  return (
  <>
    <div className="mt-7 grid gap-5 sm:grid-cols-2">
      <LeadTextField
        kind={kind}
        name="name"
        label="Name"
        value={form.name}
        onChange={update}
        required
      />

      <LeadTextField
        kind={kind}
        name="email"
        label="Email"
        value={form.email}
        onChange={update}
        required
        type="email"
      />

      <LeadTextField
        kind={kind}
        name="phone"
        label="Phone"
        value={form.phone}
        onChange={update}
      />

      {!compact && !isCareer && (
        <LeadTextField
          kind={kind}
          name="child_age"
          label="Child's age"
          value={form.child_age}
          onChange={update}
        />
      )}

      {!isCareer && (
        <LeadTextField
          kind={kind}
          name="insurance"
          label="Insurance"
          value={form.insurance}
          onChange={update}
        />
      )}

        <LeadTextField kind={kind} name="city" label="City" value={form.city} onChange={update} />
      </div>

      <div className="mt-5 space-y-2">
        <Label htmlFor={`${kind}-message`} className="text-navy" data-testid={`${kind}-message-label`}>
          {isCareer ? "Tell us about your experience" : "How can we help?"}
        </Label>
        <Textarea
          id={`${kind}-message`}
          name="message"
          value={form.message}
          onChange={update}
          className="min-h-32 rounded-2xl border-navy/10 bg-slate px-4 py-3"
          data-testid={`${kind}-message-textarea`}
        />
      </div>
    </>
  );
}

function LeadForm({ kind = "client", title = "Tell us how we can help", compact = false }) {
  const { form, submitting, update, submit } = useLeadForm(kind);

  return (
    <form onSubmit={submit} className="rounded-[2rem] bg-white p-6 shadow-xl shadow-navy/10 md:p-8" data-testid={`${kind}-lead-form`}>
      <h2 className="font-display text-2xl font-semibold text-navy" data-testid={`${kind}-lead-form-title`}>{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-navy/65" data-testid={`${kind}-lead-form-copy`}>Share a few details and we’ll help you find the right next step.</p>
      <LeadFields kind={kind} form={form} update={update} compact={compact} />
      <Button type="submit" disabled={submitting} className="mt-6 w-full rounded-full bg-teal py-6 font-bold text-white hover:bg-teal-dark" data-testid={`${kind}-submit-button`}>
        {submitting ? "Sending..." : "Submit Request"}
      </Button>
    </form>
  );
}

function ServiceDetailPage({ service }) {
  const Icon = service.icon;

  return (
    <main data-testid={`${service.title.toLowerCase().replaceAll(" ", "-")}-page-main`}>
      <PageHero
        eyebrow={service.shortTitle}
        title={service.pageTitle}
        text={service.pageText}
        image={service.image}
        testId={`${service.title.toLowerCase().replaceAll(" ", "-")}-page-hero`}
      />

      <section className="bg-white py-20 md:py-32">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 md:grid-cols-[0.75fr_1.25fr] md:px-8">
          <div className="md:sticky md:top-28">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-teal/10 text-teal">
              <Icon className="h-8 w-8" />
            </div>
            <SectionLabel testId="service-detail-overview-eyebrow">How we help</SectionLabel>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl">
              {service.sectionTitle}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-navy/75 md:text-lg">
              {service.sectionText}
            </p>
            <Button asChild className="mt-8 rounded-full bg-teal px-8 py-6 font-bold text-white hover:bg-teal-dark">
              <Link to="/become-a-client">Ask About This Service</Link>
            </Button>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {service.focusAreas.map(([FocusIcon, title, copy], index) => (
              <Card key={title} className="rounded-[1.75rem] border-navy/5 bg-slate p-7 shadow-none transition duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardContent className="p-0">
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-teal shadow-sm">
                    <FocusIcon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-navy">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/70">{copy}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <SectionLabel testId="service-detail-process-eyebrow">What to expect</SectionLabel>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl">
              A clear, collaborative process.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-4">
            {service.steps.map((step, index) => (
              <div key={step} className="rounded-[1.75rem] bg-white p-7 shadow-sm">
                <div className="mb-7 flex h-11 w-11 items-center justify-center rounded-full bg-navy font-display text-lg font-semibold text-white">
                  {index + 1}
                </div>
                <p className="text-sm leading-relaxed text-navy/75">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[0.75fr_1.25fr] md:px-8">
          <div>
            <SectionLabel testId="service-detail-faq-eyebrow">Questions families ask</SectionLabel>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl">
              Helpful answers about {service.title.toLowerCase()}.
            </h2>
          </div>
          <Accordion type="single" collapsible className="rounded-[1.75rem] bg-slate p-4">
            {service.faqs.map(([question, answer], index) => (
              <AccordionItem key={question} value={`service-faq-${index}`} className="border-navy/10 px-4">
                <AccordionTrigger className="py-6 text-left font-display text-lg font-semibold text-navy hover:no-underline">
                  {question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-base leading-relaxed text-navy/70">
                  {answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}

function HomePage() {
  return (
    <>

      <main data-testid="homepage-main">
        <Hero />
        <InsurancePreview />
        <ParentConnection />
        <ServicesSection />
        <ProcessSection />
        <WhyChooseUs />
        <TeamSection />
        <Testimonials />
        <FAQSection />
        <FinalCTA />
      </main>
    </>
  );
}

function ServicesPage() {
  return (
    <>

      <main data-testid="services-page-main">
        <PageHero
          eyebrow="Services"
          title="Therapy, training, and collaboration built around your child."
          text="Explore the core services Au-Some Teacher ABA Services provides for families, schools, and children across Greater Houston."
          image={assets.services}
          testId="services-page-hero"
        />
        <ServicesSection detailed />
        <ProcessSection />
        <FinalCTA />
      </main>
    </>
  );
}

function InsurancePage() {
  return (
    <>

    <main data-testid="insurance-page-main">
      <PageHero
        eyebrow="Insurance"
        title="We help families understand benefits before services begin."
        text="Insurance can feel confusing. Our team helps verify benefits, explain authorization steps, and guide families through the process with clarity."
        image={assets.parent}
        testId="insurance-page-hero"
      />
      <section className="bg-white py-20 md:py-32" data-testid="insurance-detail-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[0.8fr_1.2fr] md:px-8">
          <div>
            <SectionLabel testId="insurance-detail-eyebrow">Accepted plans</SectionLabel>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="insurance-detail-title">Start with a benefit check.</h2>
            <p className="mt-5 text-base leading-relaxed text-navy/75" data-testid="insurance-detail-copy">We currently list Texas Children&apos;s Health Plan, Blue Cross Blue Shield of Texas, and Aetna. Plan coverage varies, so verification is the best first step.</p>
          </div>
          <LeadForm kind="insurance" title="Verify your benefits" compact />
        </div>
      </section>
      <FAQSection />
      </main>
    </>
  );
}

function AboutPage() {
  return (
    <main data-testid="about-page-main">
      <PageHero
        eyebrow="About Au-Some Teacher"
        title="Education-informed ABA care with heart, clarity, and purpose."
        text="Our mission is to help children build meaningful skills while supporting the families, teachers, and communities around them."
        image={assets.about}
        testId="about-page-hero"
      />
      <section className="bg-white py-20 md:py-32" data-testid="about-values-section">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ["Mission", "To provide evidence-based ABA therapy that helps children communicate, connect, learn, and become more independent."],
              ["Vision", "A Greater Houston community where children with autism and their families feel supported, included, and confident."],
              ["Values", "Family partnership, clinical excellence, inclusion, dignity, meaningful progress, and education-informed care."],
            ].map(([title, copy], index) => (
              <Card key={title} className="rounded-[1.75rem] border-navy/5 bg-slate p-8 shadow-none" data-testid={`about-value-card-${index + 1}`}>
                <CardContent className="p-0">
                  <h2 className="font-display text-2xl font-semibold text-navy" data-testid={`about-value-card-${index + 1}-title`}>{title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-navy/70" data-testid={`about-value-card-${index + 1}-copy`}>{copy}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      <TeamSection />
      <FAQSection />
    </main>
  );
}


const careerBenefits = [
  {
    title: "Supportive Leadership",
    description:
      "Receive guidance, feedback, and ongoing support from experienced clinical leaders.",
  },
  {
    title: "Flexible Scheduling",
    description:
      "Build a schedule that supports your professional goals and personal responsibilities.",
  },
  {
    title: "Meaningful Work",
    description:
      "Help children strengthen communication, independence, social, and daily living skills.",
  },
  {
    title: "Professional Growth",
    description:
      "Develop your ABA skills through supervision, coaching, and hands-on experience.",
  },
];

const openPositions = [
  {
    title: "Registered Behavior Technician",
    type: "Part-Time",
    schedule: "Afternoon and evening availability",
    location: "Spring and surrounding communities",
    description:
      "Provide one-on-one ABA services to children in home and community settings while working under the supervision of a BCBA.",
  },
];
function CareerApplicationWizard() {
  const steps = [
  "Contact Information",
  "Minimum Qualifications",
  "Experience",
  "Availability",
  "Service Area & Travel",
  "Professional Expectations",
  "References",
  "Certification",
  "Review",
];

  const [currentStep, setCurrentStep] = useState(1);
  const [validationError, setValidationError] = useState("");
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [application, setApplication] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    linkedin: "",
    bacbNumber: "",
    over18: "",
    education: "",
    workAuthorized: "",
    sponsorshipRequired: "",
    reliableTransportation: "",
    licenseAndInsurance: "",
    backgroundCheckConsent: "",
    position: "Registered Behavior Technician",
    rbtStatus: "",
    abaExperience: "",
    experienceSummary: "",
    weekdayAvailability: "",
    weekendAvailability: "",
    transportation: "",
    availableDays: [],
    availableTimes: [],
    serviceAreas: [],
    workSettings: [],
    clientNeeds: [],
    certificationStatements: [],
    preferredHours: "",
    startAvailability: "",
    consistentSchedule: "",
    maximumDrive: "",
    independentWork: "",
    documentation: "",
    feedback: "",
    attendance: "",
    reference1Name: "",
    reference1Relationship: "",
    reference1Email: "",
    reference1Phone: "",
    reference2Name: "",
    reference2Relationship: "",
    reference2Email: "",
    reference2Phone: "",
    certifyTruthfulness: false,
    certifyBackground: false,
    certifyPolicies: false,

electronicSignature: "",
    consent: false,
  });

  const updateApplication = (event) => {
  const { name, value, type, checked } = event.target;

  setApplication((current) => {
    if (type === "checkbox" && Array.isArray(current[name])) {
      return {
        ...current,
        [name]: checked
          ? [...current[name], value]
          : current[name].filter((item) => item !== value),
      };
    }

    return {
      ...current,
      [name]: type === "checkbox" ? checked : value,
    };
  });
};
const validateCurrentStep = () => {
  switch (currentStep) {
    case 1:
      return (
        application.firstName.trim() &&
        application.lastName.trim() &&
        application.email.trim() &&
        application.phone.trim() &&
        application.city.trim()
      );

    case 2:
      return (
        application.rbtStatus &&
        application.over18 &&
        application.education &&
        application.workAuthorized &&
        application.sponsorshipRequired &&
        application.reliableTransportation &&
        application.licenseAndInsurance &&
        application.backgroundCheckConsent
      );

    case 3:
      return (
        application.abaExperience &&
        application.workSettings.length > 0 &&
        application.clientNeeds.length > 0 &&
        application.experienceSummary.trim()
      );

    case 4:
      return (
        application.availableDays.length > 0 &&
        application.availableTimes.length > 0 &&
        application.preferredHours &&
        application.startAvailability &&
        application.consistentSchedule
      );

    case 5:
      return (
        application.serviceAreas.length > 0 &&
        application.maximumDrive
      );

    case 6:
      return (
        application.independentWork &&
        application.documentation &&
        application.feedback &&
        application.attendance
      );

    case 7:
      return (
        application.reference1Name.trim() &&
        application.reference1Relationship.trim() &&
        application.reference1Email.trim() &&
        application.reference1Phone.trim() &&
        application.reference2Name.trim() &&
        application.reference2Relationship.trim() &&
        application.reference2Email.trim() &&
        application.reference2Phone.trim()
      );

    case 8:
      return (
        application.certifyTruthfulness &&
        application.certifyBackground &&
        application.certifyPolicies &&
        application.electronicSignature.trim()
      );

    default:
      return true;
  }
};
const fieldHasError = (fieldName) => {
  if (!showValidationErrors) {
    return false;
  }

  const value = application[fieldName];

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "boolean") {
    return value === false;
  }

  return !value || !String(value).trim();
};
  const goNext = () => {
  setShowValidationErrors(true);

  if (!validateCurrentStep()) {
    setValidationError("Please complete all required fields before continuing.");
    return;
  }

  setValidationError("");
  setShowValidationErrors(false);

  if (currentStep < steps.length) {
    setCurrentStep((step) => step + 1);
  }
};

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep((step) => step - 1);
    }
  };
const submitApplication = async () => {
  try {
    await fetch(
      "https://script.google.com/macros/s/AKfycby-dOKvpD5oBIqOfwN3D_OOzu2W6lf8dLLfFHLlpZS4ISnBsBw3w3ldHQo4mbBadNMbwA/exec",
      {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(application),
      }
    );

    toast.success("Application submitted successfully!", {
      description:
        "Thank you for applying. We have received your application.",
    });
  } catch (error) {
    console.error(error);

    toast.error("Submission failed", {
      description: "Unable to connect to the application server.",
    });
  }
};
  const progressPercent = (currentStep / steps.length) * 100;

  return (
    <div className="overflow-hidden rounded-[2rem] bg-white shadow-2xl shadow-navy/10">
      <div className="bg-navy px-6 py-8 text-white md:px-10">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-gold">
          Employment Application
        </p>

        <h2 className="mt-3 font-display text-3xl font-semibold">
          Join the Au-Some Teacher team
        </h2>

        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/70">
          Complete the application below. Your information will be saved as you
          move through each section.
        </p>
      </div>

      <div className="border-b border-navy/10 px-6 py-6 md:px-10">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-bold text-navy">
            Step {currentStep} of {steps.length}
          </p>

          <p className="text-sm text-navy/60">
            {steps[currentStep - 1]}
          </p>
        </div>

        <div className="h-3 overflow-hidden rounded-full bg-slate">
          <div
            className="h-full rounded-full bg-teal transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="px-6 py-8 md:px-10">
        {validationError && (
  <div className="mb-6 rounded-2xl border border-red-300 bg-red-50 p-4">
    <p className="text-sm font-medium text-red-700">
      {validationError}
    </p>
  </div>
)}
        {currentStep === 1 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Contact Information
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      Enter the information that we should use throughout the hiring process.
    </p>

    <div className="mt-7 grid gap-5 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="career-first-name">
          First name <span className="text-red-600">*</span>
        </Label>

        <Input
          id="career-first-name"
          name="firstName"
          value={application.firstName}
          onChange={updateApplication}
          required
          className={`h-12 rounded-2xl bg-slate ${
  fieldHasError("firstName")
    ? "border-red-500 ring-2 ring-red-200"
    : "border-navy/10"
}`}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="career-last-name">
          Last name <span className="text-red-600">*</span>
        </Label>

        <Input
          id="career-last-name"
          name="lastName"
          value={application.lastName}
          onChange={updateApplication}
          required
          className="h-12 rounded-2xl border-navy/10 bg-slate"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="career-email">
          Email address <span className="text-red-600">*</span>
        </Label>

        <Input
          id="career-email"
          name="email"
          type="email"
          value={application.email}
          onChange={updateApplication}
          required
          className="h-12 rounded-2xl border-navy/10 bg-slate"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="career-phone">
          Mobile phone number <span className="text-red-600">*</span>
        </Label>

        <Input
          id="career-phone"
          name="phone"
          type="tel"
          value={application.phone}
          onChange={updateApplication}
          required
          className="h-12 rounded-2xl border-navy/10 bg-slate"
        />
      </div>

      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="career-city">
          City and ZIP code <span className="text-red-600">*</span>
        </Label>

        <Input
          id="career-city"
          name="city"
          value={application.city}
          onChange={updateApplication}
          placeholder="Spring, TX 77386"
          required
          className="h-12 rounded-2xl border-navy/10 bg-slate"
        />

        <p className="text-xs text-navy/55">
          Do not enter your full street address.
        </p>
      </div>

      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="career-linkedin">
          LinkedIn profile or professional website
          <span className="ml-1 text-xs font-normal text-navy/50">
            Optional
          </span>
        </Label>

        <Input
          id="career-linkedin"
          name="linkedin"
          type="url"
          value={application.linkedin || ""}
          onChange={updateApplication}
          placeholder="https://www.linkedin.com/in/your-profile"
          className="h-12 rounded-2xl border-navy/10 bg-slate"
        />
      </div>
    </div>
  </div>
)}
        {currentStep === 2 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Minimum Qualifications
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      These questions help determine whether the position matches your current qualifications.
    </p>

    <div className="mt-7 grid gap-5">

      <div className="space-y-2">
        <Label>Are you currently certified as a Registered Behavior Technician through the BACB? *</Label>

        <select
          name="rbtStatus"
          value={application.rbtStatus}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>Yes, my certification is active</option>
          <option>My certification is inactive or expired</option>
          <option>I completed the 40-hour course but have not passed the exam</option>
          <option>No, I am not currently certified</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>BACB certification number</Label>

        <Input
          name="bacbNumber"
          value={application.bacbNumber || ""}
          onChange={updateApplication}
          placeholder="RBT-24-348734"
          className="h-12 rounded-2xl border-navy/10 bg-slate"
        />

        <p className="text-xs text-navy/55">
          Leave blank if you are not currently certified.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Are you at least 18 years old? *</Label>

        <select
          name="over18"
          value={application.over18 || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>Yes</option>
          <option>No</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>Highest completed education *</Label>

        <select
          name="education"
          value={application.education || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>High school diploma or GED</option>
          <option>Some college</option>
          <option>Associate degree</option>
          <option>Bachelor's degree</option>
          <option>Graduate degree</option>
          <option>I have not completed high school or a GED</option>
        </select>
      </div>
          <div className="space-y-2">
  <Label>Are you legally authorized to work in the United States? *</Label>

  <select
    name="workAuthorized"
    value={application.workAuthorized || ""}
    onChange={updateApplication}
    className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
  >
    <option value="">Select one</option>
    <option>Yes</option>
    <option>No</option>
  </select>
</div>

<div className="space-y-2">
  <Label>Will you now or in the future require employment sponsorship? *</Label>

  <select
    name="sponsorshipRequired"
    value={application.sponsorshipRequired || ""}
    onChange={updateApplication}
    className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
  >
    <option value="">Select one</option>
    <option>Yes</option>
    <option>No</option>
  </select>
</div>

<div className="space-y-2">
  <Label>Do you have reliable transportation to travel between client locations? *</Label>

  <select
    name="reliableTransportation"
    value={application.reliableTransportation || ""}
    onChange={updateApplication}
    className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
  >
    <option value="">Select one</option>
    <option>Yes</option>
    <option>No</option>
  </select>
</div>

<div className="space-y-2">
  <Label>Do you have a valid driver's license and current automobile insurance? *</Label>

  <select
    name="licenseAndInsurance"
    value={application.licenseAndInsurance || ""}
    onChange={updateApplication}
    className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
  >
    <option value="">Select one</option>
    <option>Yes</option>
    <option>No</option>
  </select>
</div>

<div className="space-y-2">
  <Label>Are you willing to complete all required background checks? *</Label>

  <select
    name="backgroundCheckConsent"
    value={application.backgroundCheckConsent || ""}
    onChange={updateApplication}
    className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
  >
    <option value="">Select one</option>
    <option>Yes</option>
    <option>No</option>
  </select>
</div>
    </div>
  </div>
)}

        {currentStep === 3 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Experience
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      Tell us about your relevant professional experience.
    </p>

    <div className="mt-7 grid gap-5">

      <div className="space-y-2">
        <Label>
          How much direct ABA or RBT experience do you have? *
        </Label>

        <select
          name="abaExperience"
          value={application.abaExperience}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>No direct ABA experience</option>
          <option>Less than 6 months</option>
          <option>6–11 months</option>
          <option>1–2 years</option>
          <option>3–4 years</option>
          <option>5 or more years</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>
          Which settings have you worked in? *
        </Label>

        <Textarea
          name="workSettings"
          value={application.workSettings || ""}
          onChange={updateApplication}
          placeholder="Example: In-home ABA, Clinic, Public School"
          className="min-h-24 rounded-2xl border-navy/10 bg-slate"
        />

        <p className="text-xs text-navy/55">
          Separate multiple settings with commas.
        </p>
      </div>

      <div className="space-y-2">
        <Label>
          Which client needs have you supported? *
        </Label>

        <Textarea
          name="clientNeeds"
          value={application.clientNeeds || ""}
          onChange={updateApplication}
          placeholder="Example: Early learners, Social skills, Aggression"
          className="min-h-24 rounded-2xl border-navy/10 bg-slate"
        />

        <p className="text-xs text-navy/55">
          Separate multiple selections with commas.
        </p>
      </div>

      <div className="space-y-2">
        <Label>
          Briefly describe your experience working with children or individuals with developmental disabilities. *
        </Label>

        <Textarea
          name="experienceSummary"
          value={application.experienceSummary}
          onChange={updateApplication}
          className="min-h-40 rounded-2xl border-navy/10 bg-slate"
        />
      </div>

    </div>
  </div>
)}      
        {currentStep === 4 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Availability
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      Our greatest need is generally during after-school and evening hours.
    </p>

    <div className="mt-7 grid gap-7">
      <div className="space-y-3">
        <Label>
          Which days are you regularly available? *
        </Label>

        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ].map((day) => (
            <label
              key={day}
              className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-slate p-4"
            >
              <input
                type="checkbox"
                name="availableDays"
                value={day}
                checked={application.availableDays.includes(day)}
                onChange={updateApplication}
                className="h-4 w-4"
              />

              <span className="text-sm text-navy">
                {day}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label>
          Which time periods are you regularly available? *
        </Label>

        <div className="grid gap-3">
          {[
            "Weekday mornings",
            "Weekday afternoons before 3:00 PM",
            "Weekday afternoons from 3:00–5:00 PM",
            "Weekday evenings from 5:00–8:00 PM",
            "Saturday mornings",
            "Saturday afternoons",
            "Sunday",
          ].map((timePeriod) => (
            <label
              key={timePeriod}
              className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-slate p-4"
            >
              <input
                type="checkbox"
                name="availableTimes"
                value={timePeriod}
                checked={application.availableTimes.includes(timePeriod)}
                onChange={updateApplication}
                className="h-4 w-4"
              />

              <span className="text-sm text-navy">
                {timePeriod}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>
          How many hours per week are you seeking? *
        </Label>

        <select
          name="preferredHours"
          value={application.preferredHours || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4 text-sm text-navy"
        >
          <option value="">Select one</option>
          <option>Fewer than 10 hours</option>
          <option>10–15 hours</option>
          <option>16–20 hours</option>
          <option>21–25 hours</option>
          <option>More than 25 hours</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>
          When could you begin working? *
        </Label>

        <select
          name="startAvailability"
          value={application.startAvailability || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4 text-sm text-navy"
        >
          <option value="">Select one</option>
          <option>Immediately</option>
          <option>Within one week</option>
          <option>Within two weeks</option>
          <option>Within 30 days</option>
          <option>More than 30 days from now</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>
          Are you able to maintain a consistent weekly schedule for at least six months? *
        </Label>

        <select
          name="consistentSchedule"
          value={application.consistentSchedule || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4 text-sm text-navy"
        >
          <option value="">Select one</option>
          <option>Yes</option>
          <option>No</option>
          <option>Unsure</option>
        </select>
      </div>
    </div>
  </div>
)}

        {currentStep === 5 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Service Area & Travel
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      RBT services are provided at client homes and community locations.
    </p>

    <div className="mt-7 grid gap-7">
      <div className="space-y-3">
        <Label>
          Which areas are you willing to serve? *
        </Label>

        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Spring",
            "Klein",
            "The Woodlands",
            "Shenandoah",
            "Humble",
            "Conroe",
            "Other nearby area",
          ].map((area) => (
            <label
              key={area}
              className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-slate p-4"
            >
              <input
                type="checkbox"
                name="serviceAreas"
                value={area}
                checked={application.serviceAreas.includes(area)}
                onChange={updateApplication}
                className="h-4 w-4"
              />

              <span className="text-sm text-navy">
                {area}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>
          What is the maximum one-way drive you are normally willing to make? *
        </Label>

        <select
          name="maximumDrive"
          value={application.maximumDrive || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4 text-sm text-navy"
        >
          <option value="">Select one</option>
          <option>Up to 10 miles</option>
          <option>Up to 15 miles</option>
          <option>Up to 20 miles</option>
          <option>Up to 25 miles</option>
          <option>More than 25 miles</option>
        </select>
      </div>
    </div>
  </div>
)}
        {currentStep === 6 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Professional Expectations
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      These questions help us understand how you approach professional responsibilities.
    </p>

    <div className="mt-7 grid gap-5">

      <div className="space-y-2">
        <Label>
          Are you comfortable working independently in client homes while receiving BCBA supervision? *
        </Label>

        <select
          name="independentWork"
          value={application.independentWork || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>Yes</option>
          <option>No</option>
          <option>I would like more information</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>
          Are you able to complete session documentation accurately and on time? *
        </Label>

        <select
          name="documentation"
          value={application.documentation || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>Yes</option>
          <option>No</option>
          <option>I would need training</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>
          Are you comfortable receiving corrective feedback and performance coaching? *
        </Label>

        <select
          name="feedback"
          value={application.feedback || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>Yes</option>
          <option>No</option>
          <option>Unsure</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label>
          Do you understand that consistent attendance is essential because clients depend on regularly scheduled services? *
        </Label>

        <select
          name="attendance"
          value={application.attendance || ""}
          onChange={updateApplication}
          className="h-12 w-full rounded-2xl border border-navy/10 bg-slate px-4"
        >
          <option value="">Select one</option>
          <option>Yes</option>
          <option>No</option>
        </select>
      </div>

    </div>
  </div>
)}
        {currentStep === 7 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Professional References
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      Please provide two professional references who can speak to your work,
      reliability, and professionalism.
    </p>

    <div className="mt-7 grid gap-8">
      <div className="rounded-3xl border border-navy/10 bg-slate/60 p-5">
        <h4 className="font-semibold text-navy">Reference 1</h4>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Full name *</Label>
            <Input
              name="reference1Name"
              value={application.reference1Name || ""}
              onChange={updateApplication}
            />
          </div>

          <div className="space-y-2">
            <Label>Relationship *</Label>
            <Input
              name="reference1Relationship"
              value={application.reference1Relationship || ""}
              onChange={updateApplication}
              placeholder="Supervisor, professor, coworker, etc."
            />
          </div>

          <div className="space-y-2">
            <Label>Email address *</Label>
            <Input
              type="email"
              name="reference1Email"
              value={application.reference1Email || ""}
              onChange={updateApplication}
            />
          </div>

          <div className="space-y-2">
            <Label>Phone number *</Label>
            <Input
              type="tel"
              name="reference1Phone"
              value={application.reference1Phone || ""}
              onChange={updateApplication}
            />
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-navy/10 bg-slate/60 p-5">
        <h4 className="font-semibold text-navy">Reference 2</h4>

        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Full name *</Label>
            <Input
              name="reference2Name"
              value={application.reference2Name || ""}
              onChange={updateApplication}
            />
          </div>

          <div className="space-y-2">
            <Label>Relationship *</Label>
            <Input
              name="reference2Relationship"
              value={application.reference2Relationship || ""}
              onChange={updateApplication}
              placeholder="Supervisor, professor, coworker, etc."
            />
          </div>

          <div className="space-y-2">
            <Label>Email address *</Label>
            <Input
              type="email"
              name="reference2Email"
              value={application.reference2Email || ""}
              onChange={updateApplication}
            />
          </div>

          <div className="space-y-2">
            <Label>Phone number *</Label>
            <Input
              type="tel"
              name="reference2Phone"
              value={application.reference2Phone || ""}
              onChange={updateApplication}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
)}
        {currentStep === 8 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Applicant Certification
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      Please review the statements below before submitting your application.
    </p>

    <div className="mt-7 space-y-4">

      <label className="flex items-start gap-3 rounded-2xl border border-navy/10 bg-slate p-5">
        <input
          type="checkbox"
          name="certifyTruthfulness"
          checked={application.certifyTruthfulness || false}
          onChange={updateApplication}
          className="mt-1"
        />
        <span className="text-sm text-navy">
          I certify that all information provided in this application is true
          and complete to the best of my knowledge.
        </span>
      </label>

      <label className="flex items-start gap-3 rounded-2xl border border-navy/10 bg-slate p-5">
        <input
          type="checkbox"
          name="certifyBackground"
          checked={application.certifyBackground || false}
          onChange={updateApplication}
          className="mt-1"
        />
        <span className="text-sm text-navy">
          I understand that employment may be contingent upon successful
          completion of background screening and verification of credentials.
        </span>
      </label>

      <label className="flex items-start gap-3 rounded-2xl border border-navy/10 bg-slate p-5">
        <input
          type="checkbox"
          name="certifyPolicies"
          checked={application.certifyPolicies || false}
          onChange={updateApplication}
          className="mt-1"
        />
        <span className="text-sm text-navy">
          I understand that if hired, I will be expected to comply with all
          company policies, HIPAA requirements, and BACB ethics standards.
        </span>
      </label>

      <div className="space-y-2">
        <Label>Electronic Signature *</Label>

        <Input
          name="electronicSignature"
          value={application.electronicSignature || ""}
          onChange={updateApplication}
          placeholder="Type your full legal name"
        />
      </div>

    </div>
  </div>
)}
        {currentStep === 9 && (
  <div>
    <h3 className="font-display text-2xl font-semibold text-navy">
      Review Your Application
    </h3>

    <p className="mt-2 text-sm text-navy/65">
      Please review your information before submitting.
    </p>

    <div className="mt-7 grid gap-6">
      {[
        {
          title: "Contact Information",
          items: [
            ["Name", `${application.firstName} ${application.lastName}`],
            ["Email", application.email],
            ["Phone", application.phone],
            ["City and ZIP", application.cityZip],
            ["LinkedIn or Website", application.linkedin],
          ],
        },
        {
          title: "Minimum Qualifications",
          items: [
            ["RBT Status", application.rbtStatus],
            ["BACB Number", application.bacbNumber],
            ["Age 18 or Older", application.over18],
            ["Education", application.education],
            ["Authorized to Work", application.workAuthorized],
            ["Sponsorship Required", application.sponsorshipRequired],
            ["Reliable Transportation", application.reliableTransportation],
            ["License and Insurance", application.licenseAndInsurance],
            ["Background Check Consent", application.backgroundCheckConsent],
          ],
        },
        {
          title: "Experience",
          items: [
            ["ABA Experience", application.abaExperience],
            ["Work Settings",
  Array.isArray(application.workSettings)
    ? application.workSettings.join(", ")
    : application.workSettings || ""
],

["Client Needs",
  Array.isArray(application.clientNeeds)
    ? application.clientNeeds.join(", ")
    : application.clientNeeds || ""
],
            ["Experience Summary", application.experienceSummary],
          ],
        },
        {
          title: "Availability",
          items: [
            ["Available Days", application.availableDays?.join(", ")],
            ["Available Times", application.availableTimes?.join(", ")],
            ["Preferred Hours", application.preferredHours],
            ["Start Availability", application.startAvailability],
            ["Consistent Schedule", application.consistentSchedule],
          ],
        },
        {
          title: "Service Area & Travel",
          items: [
            ["Service Areas", application.serviceAreas?.join(", ")],
            ["Maximum Drive", application.maximumDrive],
          ],
        },
        {
          title: "Professional Expectations",
          items: [
            ["Independent Work", application.independentWork],
            ["Documentation", application.documentation],
            ["Feedback and Coaching", application.feedback],
            ["Attendance Understanding", application.attendance],
          ],
        },
        {
          title: "Professional References",
          items: [
            ["Reference 1", application.reference1Name],
            ["Relationship", application.reference1Relationship],
            ["Email", application.reference1Email],
            ["Phone", application.reference1Phone],
            ["Reference 2", application.reference2Name],
            ["Relationship", application.reference2Relationship],
            ["Email", application.reference2Email],
            ["Phone", application.reference2Phone],
          ],
        },
        {
          title: "Certification",
          items: [
            [
              "Information Certified",
              application.certifyTruthfulness ? "Yes" : "No",
            ],
            [
              "Background Screening Acknowledged",
              application.certifyBackground ? "Yes" : "No",
            ],
            [
              "Policies Acknowledged",
              application.certifyPolicies ? "Yes" : "No",
            ],
            ["Electronic Signature", application.electronicSignature],
          ],
        },
      ].map((section) => (
        <div
          key={section.title}
          className="rounded-3xl border border-navy/10 bg-slate/60 p-5"
        >
          <h4 className="font-semibold text-navy">
            {section.title}
          </h4>

          <div className="mt-4 grid gap-3">
            {section.items.map(([label, value], index) => (
              <div
                key={`${section.title}-${label}-${index}`}
                className="grid gap-1 border-b border-navy/10 pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[190px_1fr]"
              >
                <span className="text-sm font-medium text-navy/70">
                  {label}
                </span>

                <span className="text-sm text-navy">
                  {value || "Not provided"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
)}

        <div className="mt-10 flex items-center justify-between gap-4 border-t border-navy/10 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={currentStep === 1}
            className="rounded-full border-navy/15 px-7 py-6 font-bold text-navy"
          >
            Previous
          </Button>

          {currentStep < steps.length ? (
            <Button
              type="button"
              onClick={goNext}
              className="rounded-full bg-teal px-7 py-6 font-bold text-white hover:bg-teal-dark"
            >
              Continue
              <Icons.ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
  type="button"
  onClick={submitApplication}
disabled={!validateCurrentStep()}  className="rounded-full bg-teal px-7 py-6 font-bold text-white hover:bg-teal-dark"
>
  Submit Application
</Button>
          )}
        </div>
      </div>
    </div>
  );
}
function Careers() {
  const [showApplication, setShowApplication] = useState(false);
  return (
    <main className="careers-page">
      <section className="careers-hero">
        <div className="careers-container careers-hero-grid">
          <div className="careers-hero-content">
            <p className="careers-eyebrow">CAREERS</p>

            <h1>Help children thrive while building a meaningful career.</h1>

            <p className="careers-hero-description">
              Join Au-Some Teacher ABA Services and become part of a
              compassionate team committed to family partnership,
              professionalism, and meaningful progress.
            </p>

            <div className="careers-highlights" aria-label="Career benefits">
              <span>Flexible scheduling</span>
              <span>Supportive BCBA leadership</span>
              <span>Mileage reimbursement</span>
              <span>Professional growth</span>
            </div>

            <div className="careers-actions">
  <button
  className="careers-button careers-button-primary"
  onClick={() => {
    document
      .getElementById("open-positions")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }}
>
  Apply
</button>

  {HIRING_OPEN && (
    <button
      type="button"
      className="careers-button careers-button-secondary"
      onClick={() =>
        document
          .getElementById("open-positions")
          ?.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    >
      View Open Positions
    </button>
  )}
</div>
</div>

          <div className="careers-hero-image-wrapper">
            <img
              className="careers-hero-image"
              src={assets.careers}
              alt="Au-Some Teacher ABA team members collaborating"
            />

            <div className="careers-image-card">
              <strong>Now hiring</strong>
              <span>Registered Behavior Technicians</span>
            </div>
          </div>
        </div>
      </section>

      <section className="careers-benefits-section">
        <div className="careers-container">
          <div className="careers-section-heading">
            <p className="careers-eyebrow">WHY JOIN US</p>
            <h2>A place to make an impact and continue growing</h2>
            <p>
              We believe quality care begins with professionals who feel
              supported, respected, and prepared to succeed.
            </p>
          </div>

          <div className="careers-benefits-grid">
            {careerBenefits.map((benefit) => (
              <article className="careers-benefit-card" key={benefit.title}>
                <div className="careers-benefit-icon" aria-hidden="true">
                  ✓
                </div>

                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    {HIRING_OPEN ? (
      <section
        className="careers-positions-section"
        id="open-positions"
        aria-labelledby="positions-heading"
      >
        <div className="careers-container">
          <div className="careers-section-heading careers-section-heading-left">
            <p className="careers-eyebrow">OPEN POSITIONS</p>
            <h2 id="positions-heading">Current opportunities</h2>
            <p>
              Explore available roles and find an opportunity that fits your
              experience and availability.
            </p>
          </div>

          <div className="careers-position-list">
            {openPositions.map((position) => (
              <article className="careers-position-card" key={position.title}>
                <div className="careers-position-main">
                  <div className="careers-position-title-row">
                    <div>
                      <p className="careers-position-type">{position.type}</p>
                      <h3>{position.title}</h3>
                    </div>

                    <span className="careers-hiring-badge">Now Hiring</span>
                  </div>

                  <p className="careers-position-description">
                    {position.description}
                  </p>

                  <div className="careers-position-details">
                    <span>
                      <strong>Schedule:</strong> {position.schedule}
                    </span>

                    <span>
                      <strong>Service area:</strong> {position.location}
                    </span>
                  </div>
                </div>

                <button
  type="button"
  className="careers-button careers-button-primary"
  onClick={() => setShowApplication(true)}
>
  Apply for This Role
</button>
              </article>
            ))}
          </div>
        </div>
      </section>
) : (
  <section
  id="open-positions"
  className="careers-positions-section"
>
    <div className="careers-container">
      <div className="careers-empty-state">
        <div style={{ fontSize: "4rem", marginBottom: "20px" }}>💙</div>

<h2>Thank You for Your Interest</h2>

<h3
  style={{
    color: "#6b7280",
    marginTop: "-10px",
    marginBottom: "24px",
  }}
>
  We're Not Hiring Right Now
</h3>
        <p>
          Thank you for your interest in joining Au-Some Teacher ABA.
          We do not have any open positions at this time, but we're always
          growing and encourage you to check back soon for future opportunities.
        </p>
      </div>
    </div>
  </section>
)}
          
  {showApplication && (
  <section className="careers-application-section" id="apply">
    <div className="careers-container">
      <CareerApplicationWizard />
    </div>
  </section>
)}
</main>
  );
}


function BecomeClientPage() {
  return (
    <main data-testid="become-client-page-main">
      <PageHero
        eyebrow="Become a Client"
        title="Take the first step toward support at home."
        text="Share your family's needs and we’ll help you understand availability, insurance, assessment, and what beginning services can look like."
        image={assets.hero}
        cta={false}
        testId="become-client-page-hero"
      />
      <section className="bg-slate py-20 md:py-32" data-testid="become-client-form-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[0.8fr_1.2fr] md:px-8">
          <div>
            <SectionLabel testId="become-client-detail-eyebrow">Getting started</SectionLabel>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy md:text-5xl" data-testid="become-client-detail-title">A simple intake designed for busy families.</h2>
            <p className="mt-5 text-base leading-relaxed text-navy/75" data-testid="become-client-detail-copy">You do not need to have every answer before reaching out. Tell us what you know, and we’ll help guide the next step.</p>
            <div className="mt-8 rounded-[1.75rem] bg-white p-6 shadow-sm" data-testid="contact-info-card">
              <Icons.PhoneCall className="mb-4 h-7 w-7 text-teal" />
              <a href={contact.emailHref} className="font-bold text-navy underline decoration-teal/40 underline-offset-4" data-testid="contact-info-email-link">{contact.email}</a>
              <p className="mt-2 text-sm text-navy/65" data-testid="contact-info-service-area">Serving families throughout the Greater Houston area.</p>
            </div>
          </div>
          <LeadForm kind="client" title="Become a client" />
        </div>
      </section>
    </main>
  );
}

function Footer() {
  return (
    <footer className="bg-navy py-16 text-white md:py-20" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 md:grid-cols-[1.2fr_0.8fr_0.8fr_1fr] md:px-8">
        <div>
          <Logo footer />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/70" data-testid="footer-description">In-home ABA therapy helping children build communication, independence, social skills, and confidence.</p>
          <p className="mt-5 font-semibold text-gold" data-testid="footer-slogan">Teaching Skills. Building Confidence. Changing Futures.</p>
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold" data-testid="footer-services-heading">Services</h3>
          <div className="mt-4 flex flex-col gap-3">
            {services.map((service) => (
              <Link key={service.title} to={service.path} className="text-sm text-white/70 hover:text-white" data-testid={`footer-service-${service.title.toLowerCase().replaceAll(" ", "-")}-link`}>{service.title}</Link>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold" data-testid="footer-company-heading">Company</h3>
          <div className="mt-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className="text-sm text-white/70 hover:text-white" data-testid={`footer-nav-${item.label.toLowerCase().replaceAll(" ", "-")}-link`}>{item.label}</Link>
            ))}
            <Link to="/become-a-client" className="text-sm text-white/70 hover:text-white" data-testid="footer-become-client-link">Become a Client</Link>
          </div>
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold" data-testid="footer-contact-heading">Contact</h3>
          <a href={contact.emailHref} className="mt-4 block text-sm text-white/70 hover:text-white" data-testid="footer-email-link">{contact.email}</a>
          <p className="mt-3 text-sm leading-relaxed text-white/70" data-testid="footer-areas">{serviceAreas.join(", ")}</p>
          <Button asChild className="mt-6 rounded-full bg-teal px-6 py-5 font-bold text-white hover:bg-teal-dark" data-testid="footer-contact-button">
            <Link to="/become-a-client">Contact Us</Link>
          </Button>
        </div>
      </div>
      <div className="mx-auto mt-12 max-w-7xl border-t border-white/10 px-4 pt-6 text-xs text-white/50 md:px-8" data-testid="footer-copyright">
        © {new Date().getFullYear()} Au-Some Teacher ABA Services. All rights reserved.
      </div>
    </footer>
  );
}

function PricingPage() {
  const familyRates = [
    ["Direct ABA Therapy (RBT)", "$90/hour"],
    ["BCBA Clinical Supervision", "$150/hour"],
    ["Parent & Caregiver Training", "$130/hour"],
    ["Functional Behavior Assessment Consultation", "$180/hour"],
    ["School Consultation & Advocacy", "$150/hour"],
    ["IEP/ARD Meeting Support", "$150/hour"],
    ["Social Skills Groups", "$60–$75/session"],
  ];
  const assessments = [
    ["Functional Assessment", "Starting at $1,500"],
    ["Comprehensive ABA Assessment", "$2,000–$2,500"],
    ["School + Home Assessment", "$2,500–$4,000"],
    ["Expedited Assessment Report", "Additional $250–$500"],
  ];
  const districts = ["Functional Behavior Assessments (FBAs)", "Behavior Intervention Plans (BIPs)", "School-Based Behavior Consultation", "Staff Training & Professional Development", "Organizational Supervision"];
  return (
    <main className="bg-slate min-h-screen">
      <section className="bg-navy px-4 py-20 text-center text-white md:py-28">
        <p className="font-semibold uppercase tracking-widest text-gold">Transparent pricing</p>
        <h1 className="mx-auto mt-4 max-w-4xl font-display text-4xl font-bold md:text-6xl">Services & Pricing</h1>
        <p className="mx-auto mt-5 max-w-2xl text-white/80">Explore private-pay services, assessments, and professional supervision. School and district services are quoted individually.</p>
      </section>
      <div className="mx-auto max-w-6xl space-y-12 px-4 py-16 md:px-8">
        <section className="rounded-3xl bg-white p-6 shadow-sm md:p-10">
          <h2 className="font-display text-3xl font-semibold text-navy">Private-Pay Family Services</h2>
          <p className="mt-3 text-navy/70">Rates below are for private-pay services. Insurance coverage, authorizations, and out-of-pocket costs vary by plan.</p>
          <div className="mt-6 divide-y divide-navy/10">{familyRates.map(([name, rate]) => <div key={name} className="flex flex-wrap justify-between gap-3 py-4"><span className="text-navy">{name}</span><strong className="text-teal">{rate}</strong></div>)}</div>
          <Link to="/become-a-client" className="mt-6 inline-block rounded-full bg-teal px-6 py-3 font-bold text-white">Request a Consultation</Link>
        </section>
        <section className="rounded-3xl bg-white p-6 shadow-sm md:p-10">
          <h2 className="font-display text-3xl font-semibold text-navy">Assessment Packages</h2>
          <p className="mt-3 text-navy/70">Final scope and cost depend on the assessment needs and setting.</p>
          <div className="mt-6 divide-y divide-navy/10">{assessments.map(([name, rate]) => <div key={name} className="flex flex-wrap justify-between gap-3 py-4"><span className="text-navy">{name}</span><strong className="text-teal">{rate}</strong></div>)}</div>
        </section>
        <section id="supervision" className="rounded-3xl bg-white p-6 shadow-sm md:p-10">
          <p className="font-bold uppercase tracking-widest text-teal">Professional growth</p>
          <h2 className="mt-2 font-display text-3xl font-semibold text-navy">BCBA Fieldwork Supervision & Mentorship</h2>
          <p className="mt-4 text-navy/75">Individualized supervision for aspiring behavior analysts, with special attention to school-based practice, ethics, assessment, intervention planning, data analysis, and collaboration with educational teams.</p>
          <div className="mt-7 rounded-2xl bg-slate p-6"><p className="text-navy/70">Individual fieldwork supervision</p><p className="mt-1 font-display text-4xl font-bold text-navy">$70<span className="text-lg font-medium">/hour</span></p><p className="mt-3 text-navy/70">Billed monthly based on actual supervision hours. No fixed monthly package required.</p></div>
          <p className="mt-5 font-semibold text-navy">Free 30-minute initial consultation</p>
          <p className="mt-3 text-sm leading-relaxed text-navy/70">Qualifying fieldwork must meet applicable BACB requirements. Classroom-based fieldwork may require written school district authorization, appropriate client oversight, and privacy safeguards. Supervision availability and eligibility are confirmed before an agreement is signed.</p>
          <a href="mailto:info@au-someteacher.com?subject=BCBA%20Fieldwork%20Supervision%20Consultation" className="mt-6 inline-block rounded-full bg-teal px-6 py-3 font-bold text-white">Request Supervision Consultation</a>
        </section>
        <section className="rounded-3xl bg-white p-6 shadow-sm md:p-10">
          <h2 className="font-display text-3xl font-semibold text-navy">Schools & Districts</h2>
          <p className="mt-3 text-navy/70">We develop customized proposals based on campus needs, project scope, and timelines.</p>
          <ul className="mt-6 grid gap-3 md:grid-cols-2">{districts.map(name => <li key={name} className="rounded-xl bg-slate px-4 py-3 text-navy">{name}</li>)}</ul>
          <a href="mailto:info@au-someteacher.com?subject=School%20or%20District%20Quote%20Request" className="mt-7 inline-block rounded-full bg-teal px-6 py-3 font-bold text-white">Request a Quote</a>
        </section>
        <p className="text-center text-sm text-navy/65">Prices may vary with service scope and written agreement. Please contact us to confirm current availability and a personalized estimate.</p>
      </div>
    </main>
  );
}

function AppShell() {
  return (
    <>
      <RouteSEO />
      <ScrollToTop />
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        {services.map((service) => (
          <Route
            key={service.path}
            path={service.path}
            element={<ServiceDetailPage service={service} />}
          />
        ))}
        <Route path="/insurance" element={<InsurancePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/employees" element={<Employees />} />
        <Route path="/about" element={<AboutPage />} />

        <Route path="/careers" element={<Careers />} />
        <Route path="/become-a-client" element={<BecomeClientPage />} />
      </Routes>
      <Footer />
      <Toaster richColors position="top-right" />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
export default App;
